import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import FileInput from "../../components/form/input/FileInput";
// import DropzoneComponent from "../../components/form/form-elements/DropZone";
import {
  w3aInitial,
  w3aConfirmDocs,
  w3aConfirmExtractions,
  w3aGetGeometry,
  w3aConfirmGeometry,
  type W3AInitialResponse,
  type W3AExtractionsResponse,
  type W3AGeometryResponse,
  type W3AConfirmGeometryResponse,
  type ExtractionEdit,
  type GeometryEdit,
} from "../../lib/api/plans";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import {
  NMDocumentsStep,
  CasingEntryForm,
  PerforationEntryForm,
  CementJobEntryForm,
  type UserCasingString,
  type UserPerforation,
  type UserCementJob,
} from "../../components/plan";
// NM C-103 START
import {
  createC103Form,
  type LeaseType,
} from "../../lib/api/c103";
// NM C-103 END
import { TX_DISTRICTS, getCountiesForDistrict, getAllCounties, getFieldsForCounty } from "../../lib/reference-data";
import AbbreviationTooltip from "../../components/ui/AbbreviationTooltip";
import JurisdictionBadge from "../../components/ui/JurisdictionBadge";

type DocumentSource = "extractions" | "user_files" | "hybrid" | "manual";
type PlugsMode = "combined" | "isolated" | "both";

type WizardStep = "config" | "documents" | "extractions" | "geometry" | "complete";

const JOB_TYPES_MAP: Record<string, string> = {
  cibp_cap: "CIBP Cement Cap",
  squeeze: "Squeeze Job",
  primary: "Primary Cement",
  plug: "Cement Plug",
  remedial: "Remedial Cement",
};

interface UploadedFiles {
  gau_file?: File;
  w2_file?: File;
  w15_file?: File;
  schematic_file?: File;
  formation_tops_file?: File;
}

/** Server-side jurisdiction ceilings for max_plug_length_ft (mirrors
 *  w3a_segmented.JURISDICTION_MAX_PLUG_LENGTH_FT). Display-only — the server
 *  clamps whatever we send. */
function jurisdictionMaxPlugLengthFt(jurisdiction: "TX" | "NM"): number {
  return jurisdiction === "NM" ? 1000 : 1250;
}

/** Parse a feet input. Returns undefined for blank/invalid/negative so the
 *  caller can OMIT the field (= "use server default") instead of sending 0. */
function parseOptionalFeet(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

export default function PlanGeneration() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { api14?: string; jurisdiction?: "TX" | "NM" } | null) ?? {};
  const { currentWorkspaceId } = useWorkspace();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>("config");
  
  // Form state (Step 1: Configuration)
  const [apiNumber, setApiNumber] = useState(prefill.api14 ?? "");
  const [jurisdiction, setJurisdiction] = useState<"TX" | "NM">(prefill.jurisdiction ?? "TX");
  const [documentSource, setDocumentSource] = useState<DocumentSource>("extractions");
  const [plugsMode, setPlugsMode] = useState<PlugsMode>("combined");
  // Combine-mode geometry limits (Trello #193). Empty string = "use the
  // server's jurisdiction default" and is OMITTED from the POST — never sent
  // as 0 (Number("") === 0, and max_gap_ft: 0 means "never combine").
  const [maxPlugLength, setMaxPlugLength] = useState<string>("");
  const [maxGapFt, setMaxGapFt] = useState<string>("300");
  const [files, setFiles] = useState<UploadedFiles>({});

  // Step 2: Document verification state
  const [initialResponse, setInitialResponse] = useState<W3AInitialResponse | null>(null);
  const [rejectedDocs, setRejectedDocs] = useState<Set<number>>(new Set());
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // Step 3: Extraction review state
  const [extractionsResponse, setExtractionsResponse] = useState<W3AExtractionsResponse | null>(null);
  const [extractionEdits, setExtractionEdits] = useState<Record<string, string | number | boolean>>({});
  const [expandedDocuments, setExpandedDocuments] = useState<Set<number>>(new Set());

  // Step 4: Geometry review state
  const [geometryResponse, setGeometryResponse] = useState<W3AGeometryResponse | null>(null);
  const [geometryEdits, setGeometryEdits] = useState<Record<string, number | null>>({});
  const [userAddedFormations, setUserAddedFormations] = useState<Array<{id: string, name: string, depth: number | null}>>([]);
  const [newFormationName, setNewFormationName] = useState("");
  const [newFormationDepth, setNewFormationDepth] = useState("");
  const [userKopDepth, setUserKopDepth] = useState<string>("");
  
  // Mechanical tools state
  const [userAddedTools, setUserAddedTools] = useState<Array<{id: string, type: string, depth: number | null, description: string}>>([]);
  const [newToolType, setNewToolType] = useState("CIBP");
  const [newToolDepth, setNewToolDepth] = useState("");
  const [newToolDescription, setNewToolDescription] = useState("");

  // Manual entry metadata fields
  const [manualDistrict, setManualDistrict] = useState("");
  const [manualCounty, setManualCounty] = useState("");
  const [manualField, setManualField] = useState("");
  const [manualLease, setManualLease] = useState("");
  const [manualWellNo, setManualWellNo] = useState("");
  const [manualTotalDepth, setManualTotalDepth] = useState("");
  const [manualHasUqw, setManualHasUqw] = useState(false);
  const [manualUqwBaseDepth, setManualUqwBaseDepth] = useState("");

  // NM-specific: User-added casing strings (for NM wells only)
  const [userAddedCasing, setUserAddedCasing] = useState<UserCasingString[]>([]);

  // NM-specific: User-added perforations (for NM wells only)
  const [userAddedPerforations, setUserAddedPerforations] = useState<UserPerforation[]>([]);

  // NM-specific: User-added historic cement jobs (for NM wells only)
  const [userAddedCementJobs, setUserAddedCementJobs] = useState<UserCementJob[]>([]);

  // NM C-103 START — NM C-103 specific state
  const [nmRegion, setNmRegion] = useState<string>('');
  const [nmSubArea, setNmSubArea] = useState<string>('');
  const [nmLeaseType, setNmLeaseType] = useState<string>('');
  const [nmFormationTops, setNmFormationTops] = useState<Array<{id: string; name: string; depth_ft: number | null}>>([]);
  const [nmCoaFigure, setNmCoaFigure] = useState<string>('');
  const [c103FormId, setC103FormId] = useState<number | null>(null);
  // NM C-103 END

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState<{ detail: string; suggestion?: string } | null>(null);

  // Auto-detect jurisdiction from API prefix
  useEffect(() => {
    const normalized = apiNumber.replace(/\D/g, "");
    if (normalized.length >= 2) {
      if (normalized.startsWith("30")) {
        setJurisdiction("NM");
      } else if (normalized.startsWith("42")) {
        setJurisdiction("TX");
      }
    }
  }, [apiNumber]);

  // NM C-103 START — derived flag for NM well detection
  const isNMWell = apiNumber.replace(/\D/g, "").startsWith("30") || jurisdiction === "NM";
  // NM C-103 END

  // NM C-103 START — reset NM state when switching away from NM
  useEffect(() => {
    if (!isNMWell) {
      setNmRegion('');
      setNmSubArea('');
      setNmLeaseType('');
      setNmFormationTops([]);
      setNmCoaFigure('');
      setC103FormId(null);
    }
  }, [isNMWell]);
  // NM C-103 END

  const handleFileChange = (fileType: keyof UploadedFiles, file: File | null) => {
    setFiles((prev) => ({
      ...prev,
      [fileType]: file || undefined,
    }));
  };

  const handleStartWizard = async () => {
    if (!apiNumber.trim()) {
      setError({ detail: "Please enter an API number" });
      return;
    }

    setError(null);
    setIsLoading(true);
    setLoadingMessage("Sourcing documents...");

    try {
      console.log("[W3A-Segmented] Step 1: Sourcing documents");

      const response = await w3aInitial({
        api10: apiNumber,
        jurisdiction: jurisdiction,
        input_mode: documentSource,
        workspace_id: currentWorkspaceId,
        gau_file: files.gau_file,
        w2_file: files.w2_file,
        w15_file: files.w15_file,
        schematic_file: files.schematic_file,
        formation_tops_file: files.formation_tops_file,
        ...(documentSource === "manual" && {
          district: manualDistrict || undefined,
          county: manualCounty || undefined,
          field_name: manualField || undefined,
          lease: manualLease || undefined,
          well_no: manualWellNo || undefined,
          total_depth: manualTotalDepth ? parseFloat(manualTotalDepth) : undefined,
          has_uqw: manualHasUqw || undefined,
          uqw_base_depth: manualUqwBaseDepth ? parseFloat(manualUqwBaseDepth) : undefined,
        }),
      });

      if (documentSource === "manual") {
        setInitialResponse(response);
        setLoadingMessage("Preparing geometry forms...");
        const geometryResp = await w3aGetGeometry(response.temp_plan_id);
        setGeometryResponse(geometryResp);
        if (geometryResp.kop_data?.kop_ft != null) {
          setUserKopDepth(String(geometryResp.kop_data.kop_ft));
        }
        setCurrentStep("geometry");
        return;
      }

      setInitialResponse(response);
      setCurrentStep("documents");

      // Fetch the combined PDF and create a blob URL for display
      try {
        const { apiGetBlob } = await import("../../lib/api-client");
        const pdfBlob = await apiGetBlob(response.combined_pdf_url, {
          cache: "no-store",
        });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        console.log("[W3A-Segmented] PDF blob created successfully");
      } catch (err) {
        console.warn("[W3A-Segmented] Failed to fetch combined PDF:", err);
      }

      console.log("[W3A-Segmented] Documents sourced:", response);
    } catch (err) {
      const body = (err as any)?.body;
      if (body && body.error === "no_documents_found") {
        setError({
          detail: body.detail || "No documents found for this well.",
          suggestion: body.suggestion,
        });
      } else {
        const msg = err instanceof Error ? err.message : "Failed to source documents";
        setError({ detail: msg });
      }
      console.error("[W3A-Segmented] Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Confirm documents and extract
  const handleConfirmDocuments = async () => {
    if (!initialResponse) return;

    setError(null);
    setIsLoading(true);
    setLoadingMessage("Extracting confirmed documents...");

    try {
      console.log("[W3A-Segmented] Step 2: Confirming documents");
      
      // Build document overrides (accept/reject)
      const documentOverrides = Array.from(rejectedDocs).map(index => ({
        source_index: index,
        action: "reject" as const,
        reason: "User rejected document",
      }));

      const response = await w3aConfirmDocs(initialResponse.temp_plan_id, {
        document_overrides: documentOverrides.length > 0 ? documentOverrides : undefined,
      });

      setExtractionsResponse(response);
      
      // Expand all documents by default
      const allDocIds = new Set(response.extractions.map(ext => Number(ext.extracted_document_id)));
      setExpandedDocuments(allDocIds);
      
      setCurrentStep("extractions");
      console.log("[W3A-Segmented] Extractions complete:", response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to extract documents";
      setError({ detail: errorMessage });
      console.error("[W3A-Segmented] Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Confirm extractions and derive geometry
  const handleConfirmExtractions = async () => {
    if (!initialResponse || !extractionsResponse) return;

    setError(null);
    setIsLoading(true);
    setLoadingMessage("Deriving well geometry...");

    try {
      console.log("[W3A-Segmented] Step 3: Confirming extractions");
      
      // Build edits array from extractionEdits state
      const edits: ExtractionEdit[] = [];
      
      Object.entries(extractionEdits).forEach(([key, value]) => {
        // key format: "extractedDocId.fieldPath"
        const [extractedDocIdStr, ...pathParts] = key.split(".");
        const extractedDocId = parseInt(extractedDocIdStr);
        const fieldPath = pathParts.join(".");
        
        // Find the original document to get original value
        const doc = extractionsResponse.extractions.find(e => e.extracted_document_id === extractedDocId);
        if (doc) {
          // Navigate to get original value from json_data
          const pathSegments = fieldPath.split(".");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let originalValue: any = doc.json_data;
          for (const segment of pathSegments) {
            if (originalValue && typeof originalValue === "object") {
              originalValue = originalValue[segment];
            } else {
              originalValue = null;
              break;
            }
          }
          
          // Only add if value actually changed
          if (originalValue !== value) {
            edits.push({
              extracted_document_id: extractedDocId,
              field_path: fieldPath,
              field_label: fieldPath.split(".").pop() || fieldPath,
              original_value: originalValue,
              edited_value: value,
              reason: "User modified extracted value",
            });
          }
        }
      });

      console.log(`[W3A-Segmented] Submitting ${edits.length} extraction edits for audit trail`);

      // Submit extraction edits
      await w3aConfirmExtractions(initialResponse.temp_plan_id, {
        edits: edits.length > 0 ? edits : undefined,
      });

      // Get geometry
      const geometryResp = await w3aGetGeometry(initialResponse.temp_plan_id);
      setGeometryResponse(geometryResp);
      if (geometryResp.kop_data?.kop_ft != null) {
        setUserKopDepth(String(geometryResp.kop_data.kop_ft));
      }
      setCurrentStep("geometry");
      console.log("[W3A-Segmented] Geometry derived:", geometryResp);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to derive geometry";
      setError({ detail: errorMessage });
      console.error("[W3A-Segmented] Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Confirm geometry and build final plan
  const handleConfirmGeometry = async () => {
    if (!initialResponse || !geometryResponse) return;

    setError(null);
    setIsLoading(true);
    setLoadingMessage("Building final plan...");

    try {
      console.log("[W3A-Segmented] Step 4: Confirming geometry and building plan");
      
      // Build geometry edits array from modifications and additions
      const edits: GeometryEdit[] = [];

      // Add edits for modified existing formations
      Object.entries(geometryEdits).forEach(([fieldId, editedValue]) => {
        // Find the original formation in geometryResponse
        const originalFormation = geometryResponse.formation_tops.find(f => f.field_id === fieldId);
        if (originalFormation && editedValue !== originalFormation.value) {
          edits.push({
            field_id: fieldId,
            field_label: originalFormation.field_label,
            original_value: originalFormation.value,
            edited_value: editedValue,
            reason: "User modified formation depth",
          });
        }
      });

      // Add user-added formations as new edits
      userAddedFormations.forEach((formation) => {
        edits.push({
          field_id: formation.id,
          field_label: formation.name,
          original_value: null,
          edited_value: formation.depth,
          reason: "User added new formation",
        });
      });

      // Add KOP depth if provided
      const kopDepthValue = userKopDepth ? parseFloat(userKopDepth) : null;
      const originalKopDepth = geometryResponse.kop_data?.kop_ft ?? null;
      if (kopDepthValue !== null || originalKopDepth !== null) {
        edits.push({
          field_id: 'kop_ft',
          field_label: 'KOP Depth',
          original_value: originalKopDepth,
          edited_value: kopDepthValue,
          reason: kopDepthValue !== null ? 'User provided KOP depth' : 'User cleared KOP depth',
        });
      }

      // Add user-added mechanical tools as new edits
      userAddedTools.forEach((tool) => {
        edits.push({
          field_id: tool.id,
          field_label: `${tool.type} @ ${tool.depth} ft`,
          original_value: null,
          edited_value: {
            type: tool.type,
            depth_ft: tool.depth,
            description: tool.description,
          },
          reason: "User added existing tool",
        });
      });

      // Add user-added casing strings (NM wells)
      userAddedCasing.forEach((casing) => {
        edits.push({
          field_id: casing.id,
          field_label: `${casing.size}" Casing ${casing.weight ? `(${casing.weight} lb/ft)` : ""}`,
          original_value: null,
          edited_value: {
            casing_type: casing.casing_type,
            size: casing.size,
            weight: casing.weight,
            hole_size: casing.hole_size,
            depth_set: casing.depth_set,
            top_of_cement: casing.top_of_cement,
            bottom_of_cement: casing.bottom_of_cement,
          },
          reason: "User added casing string (NM manual entry)",
        });
      });

      // Add user-added perforations (NM wells)
      userAddedPerforations.forEach((perf) => {
        edits.push({
          field_id: perf.id,
          field_label: `Perforation ${perf.top_ft}-${perf.bottom_ft} ft`,
          original_value: null,
          edited_value: {
            top_ft: perf.top_ft,
            bottom_ft: perf.bottom_ft,
          },
          reason: "User added perforation interval (NM manual entry)",
        });
      });

      // Add user-added cement jobs
      userAddedCementJobs.forEach((job) => {
        edits.push({
          field_id: job.id,
          field_label: `${JOB_TYPES_MAP[job.job_type] || job.job_type} (${job.cement_top_ft || "?"}-${job.cement_bottom_ft || "?"} ft)`,
          original_value: null,
          edited_value: {
            job_type: job.job_type,
            cement_top_ft: job.cement_top_ft,
            cement_bottom_ft: job.cement_bottom_ft,
            sacks: job.sacks,
            description: job.description,
          },
          reason: "User added historic cement job",
        });
      });

      console.log(`[W3A-Segmented] Submitting ${edits.length} geometry edits for audit trail`);
      console.log("[W3A-Segmented] User-added formations:", userAddedFormations);
      console.log("[W3A-Segmented] User-added tools:", userAddedTools);
      console.log("[W3A-Segmented] User-added casing (NM):", userAddedCasing);
      console.log("[W3A-Segmented] User-added perforations (NM):", userAddedPerforations);
      console.log("[W3A-Segmented] User-added cement jobs (NM):", userAddedCementJobs);
      console.log("[W3A-Segmented] All geometry edits:", edits);

      // Submit geometry edits and build plan
      const response: W3AConfirmGeometryResponse = await w3aConfirmGeometry(initialResponse.temp_plan_id, {
        edits: edits.length > 0 ? edits : undefined,
        plugs_mode: plugsMode,
        ...(parseOptionalFeet(maxPlugLength) !== undefined
          ? { max_plug_length_ft: parseOptionalFeet(maxPlugLength) }
          : {}),
        ...(parseOptionalFeet(maxGapFt) !== undefined
          ? { max_gap_ft: parseOptionalFeet(maxGapFt) }
          : {}),
      });

      // NM C-103 START — create C-103 form for NM wells
      if (isNMWell) {
        setLoadingMessage("Creating C-103 form...");
        try {
          await handleCreateC103Form();
        } catch (c103Err) {
          // Non-fatal: log but don't block plan completion
          console.warn("[NM-C103] C-103 form creation failed (non-fatal):", c103Err);
        }
      }
      // NM C-103 END

      setCurrentStep("complete");
      console.log("[W3A-Segmented] Plan built:", response);
      console.log("[W3A-Segmented] Formation tops saved:", response.formation_tops);

      // Check if plan was successfully built
      if (response.plan_built && response.plan_id) {
        console.log("[W3A-Segmented] Plan successfully generated:", response.plan_id);
        // Navigate to plan view after short delay
      setTimeout(() => {
        navigate(`/plan/${response.plan_id}`);
      }, 1500);
      } else {
        console.error("[W3A-Segmented] Plan build failed:", response.plan_build_error);
        setError({ detail: response.plan_build_error || "Plan generation failed" });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to build plan";
      setError({ detail: errorMessage });
      console.error("[W3A-Segmented] Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDocumentRejection = (index: number) => {
    setRejectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Render step indicator
  // Toggle document expansion
  const toggleDocumentExpansion = (extractedDocId: number) => {
    setExpandedDocuments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(extractedDocId)) {
        newSet.delete(extractedDocId);
      } else {
        newSet.add(extractedDocId);
      }
      return newSet;
    });
  };

  // Geometry field helpers
  const updateGeometryField = (fieldId: string, value: number | null) => {
    setGeometryEdits((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const getGeometryFieldValue = (fieldId: string, originalValue: number | null): number | null => {
    return geometryEdits[fieldId] !== undefined ? geometryEdits[fieldId] : originalValue;
  };

  // Add new user-defined formation
  const addUserFormation = () => {
    if (!newFormationName.trim()) {
      alert("Please enter a formation name");
      return;
    }

    const newFormation = {
      id: `user_formation_${Date.now()}`,
      name: newFormationName.trim(),
      depth: newFormationDepth ? parseFloat(newFormationDepth) : null,
    };

    setUserAddedFormations((prev) => [...prev, newFormation]);
    setNewFormationName("");
    setNewFormationDepth("");
  };

  // Remove user-added formation
  const removeUserFormation = (id: string) => {
    setUserAddedFormations((prev) => prev.filter((f) => f.id !== id));
  };

  // Update user-added formation
  const updateUserFormation = (id: string, field: "name" | "depth", value: string | number | null) => {
    setUserAddedFormations((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  // NM C-103 START — NM formation tops helpers
  const addFormationTop = () => {
    setNmFormationTops((prev) => [
      ...prev,
      { id: `nm_ft_${Date.now()}`, name: '', depth_ft: null },
    ]);
  };

  const updateFormationTop = (index: number, field: 'name' | 'depth_ft', value: string | number | null) => {
    setNmFormationTops((prev) =>
      prev.map((ft, i) => (i === index ? { ...ft, [field]: value } : ft))
    );
  };

  const removeFormationTop = (index: number) => {
    setNmFormationTops((prev) => prev.filter((_, i) => i !== index));
  };

  // Create C-103 form when plan geometry is confirmed for NM well
  const handleCreateC103Form = async () => {
    if (!isNMWell || !apiNumber.trim()) return;
    try {
      const form = await createC103Form({
        api_number: apiNumber,
        region: nmRegion || undefined,
        sub_area: nmSubArea || undefined,
        coa_figure: nmCoaFigure || undefined,
        lease_type: (nmLeaseType as LeaseType) || undefined,
        plan_data: {
          formation_tops: nmFormationTops,
          casing_strings: userAddedCasing,
          perforations: userAddedPerforations,
        },
      });
      setC103FormId(form.id);
      console.log("[NM-C103] Created C-103 form:", form.id);
      return form;
    } catch (err) {
      console.error("[NM-C103] Failed to create C-103 form:", err);
      throw err;
    }
  };
  // NM C-103 END

  // Helper to update an extraction field value
  const updateExtractionField = (extractedDocId: number, fieldPath: string, value: string | number | boolean) => {
    const key = `${extractedDocId}.${fieldPath}`;
    setExtractionEdits((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Helper to get current value (edited or original)
  const getExtractionFieldValue = (extractedDocId: number, fieldPath: string, originalValue: string | number | boolean | null | undefined): string | number | boolean | null | undefined => {
    const key = `${extractedDocId}.${fieldPath}`;
    return extractionEdits[key] !== undefined ? extractionEdits[key] : originalValue;
  };

  // Render a single field as an editable input
  const renderEditableField = (
    label: string,
    value: string | number | boolean | null | undefined,
    extractedDocId: number,
    fieldPath: string,
    type: "text" | "number" | "date" = "text"
  ) => {
    const currentValue = getExtractionFieldValue(extractedDocId, fieldPath, value);
    const displayValue = currentValue === null || currentValue === undefined ? "" : String(currentValue);

    return (
      <div key={fieldPath} className="flex items-center gap-3 py-2">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 w-48 flex-shrink-0">
          {label}:
        </label>
        <input
          type={type}
          value={displayValue}
          onChange={(e) => {
            const newValue = type === "number" && e.target.value ? parseFloat(e.target.value) : e.target.value;
            updateExtractionField(extractedDocId, fieldPath, newValue);
          }}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      </div>
    );
  };

  // Render a section of extraction data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSection = (title: string, data: Record<string, any>, extractedDocId: number, parentPath: string) => {
    if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
      return null;
    }

    return (
      <div className="space-y-2">
        <h5 className="text-sm font-bold text-gray-800 dark:text-white/90 border-b border-gray-200 dark:border-gray-700 pb-2 mb-3">
          *** {title} ***
        </h5>
        {Object.entries(data).map(([key, value]) => {
          const fieldPath = parentPath ? `${parentPath}.${key}` : key;
          const label = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

          // Skip null or deeply nested objects for now
          if (value === null) {
            return renderEditableField(label, "", extractedDocId, fieldPath);
          } else if (typeof value === "object" && !Array.isArray(value)) {
            // Nested object - render inline fields
            return (
              <div key={key} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</p>
                {Object.entries(value).map(([nestedKey, nestedValue]) => {
                  const nestedPath = `${fieldPath}.${nestedKey}`;
                  const nestedLabel = nestedKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                  if (typeof nestedValue !== "object" || nestedValue === null) {
                    return renderEditableField(nestedLabel, nestedValue as string | number | boolean | null, extractedDocId, nestedPath);
                  }
                  return null;
                })}
              </div>
            );
          } else if (Array.isArray(value)) {
            // Array - show count and expandable details
            return (
              <div key={key} className="py-2">
                <details className="group">
                  <summary className="cursor-pointer text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                    {label} ({value.length} items)
                  </summary>
                  <div className="mt-2 pl-4 space-y-3 border-l-2 border-brand-200 dark:border-brand-800">
                    {value.map((item, idx) => {
                      const deleteKey = `${extractedDocId}.${fieldPath}.${idx}`;
                      const isDeleted = extractionEdits[deleteKey] === "__DELETE__";
                      if (isDeleted) return null;
                      return (
                        <div key={idx} className="space-y-2 pb-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">Item {idx + 1}</p>
                            <button
                              onClick={() => updateExtractionField(extractedDocId, `${fieldPath}.${idx}`, "__DELETE__")}
                              className="text-red-500 hover:text-red-700 text-sm font-bold leading-none px-1"
                              title="Remove this item"
                              type="button"
                            >
                              &times;
                            </button>
                          </div>
                          {typeof item === "object" && Object.entries(item).map(([itemKey, itemValue]) => {
                            const itemPath = `${fieldPath}.${idx}.${itemKey}`;
                            const itemLabel = itemKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                            if (typeof itemValue !== "object" || itemValue === null) {
                              return renderEditableField(itemLabel, itemValue as string | number | boolean | null, extractedDocId, itemPath);
                            }
                            return null;
                          })}
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            );
          } else {
            // Simple value - render as input
            const fieldType = typeof value === "number" ? "number" : "text";
            return renderEditableField(label, value, extractedDocId, fieldPath, fieldType);
          }
        })}
      </div>
    );
  };

  // Main extraction data renderer
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderExtractionSections = (jsonData: Record<string, any>, extractedDocId: number) => {
    // Get all keys from jsonData, excluding internal/system fields
    const allKeys = Object.keys(jsonData).filter(key => 
      !key.startsWith('_') && key !== 'human_readable_summary'
    );

    return (
      <div className="space-y-6">
        {allKeys.map((key) => {
          const value = jsonData[key];
          const title = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

          // Skip null/undefined
          if (value === null || value === undefined) {
            return null;
          }

          // String fields (like header, certifications, instructions)
          if (typeof value === "string") {
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <h5 className="text-sm font-bold text-gray-800 dark:text-white/90 border-b border-gray-200 dark:border-gray-700 pb-2 mb-3">
                  *** {title} ***
                </h5>
                <textarea
                  value={String(getExtractionFieldValue(extractedDocId, key, value) || "")}
                  onChange={(e) => updateExtractionField(extractedDocId, key, e.target.value)}
                  rows={Math.min(Math.max(3, value.split('\n').length), 8)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            );
          }

          // Array fields
          if (Array.isArray(value) && value.length > 0) {
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                {renderSection(title, { [key]: value }, extractedDocId, "")}
              </div>
            );
          }

          // Object fields (nested data like operator_info, well_info)
          if (typeof value === "object" && Object.keys(value).length > 0) {
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                {renderSection(title, value, extractedDocId, key)}
              </div>
            );
          }

          // Simple values (numbers, booleans)
          if (typeof value === "number" || typeof value === "boolean") {
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                {renderEditableField(title, value, extractedDocId, key, typeof value === "number" ? "number" : "text")}
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  const renderStepIndicator = () => {
    const steps = documentSource === "manual"
      ? [
          { id: "config", label: "Configuration" },
          { id: "geometry", label: "Geometry" },
          { id: "complete", label: "Complete" },
        ]
      : [
          { id: "config", label: "Configuration" },
          { id: "documents", label: "Documents" },
          { id: "extractions", label: "Extractions" },
          { id: "geometry", label: "Geometry" },
          { id: "complete", label: "Complete" },
        ];

    const currentIndex = steps.findIndex(s => s.id === currentStep);

    return (
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  index <= currentIndex
                    ? "border-brand-500 bg-brand-500 text-white"
                    : "border-gray-300 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-800"
                }`}
              >
                {index < currentIndex ? "✓" : index + 1}
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  index <= currentIndex ? "text-gray-900 dark:text-white" : "text-gray-500"
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  index < currentIndex ? "bg-brand-500" : "bg-gray-300 dark:bg-gray-600"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <PageMeta
        title="Create Plan | RegulAgent"
        description="Generate W-3A plugging plans"
      />

      <PageBreadcrumb pageTitle="Create Plugging Plan" />

      <div className="space-y-6">
        {/* Step Indicator */}
        {renderStepIndicator()}

            {/* Error Message */}
            {error && (
              <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
                <p className="text-sm font-medium text-error-700 dark:text-error-400">
                  {error.detail}
                </p>
                {error.suggestion && (
                  <p className="mt-2 text-sm text-error-600 dark:text-error-300">
                    {error.suggestion}
                  </p>
                )}
              </div>
            )}

        {/* Step 1: Configuration */}
        {currentStep === "config" && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Plan Configuration
                </h3>
                <JurisdictionBadge jurisdiction={jurisdiction} formType={jurisdiction === "NM" ? "c103" : "w3a"} />
              </div>
            </div>

            <div className="p-6 space-y-6">

            {/* API Number Input */}
            <div>
              <Label>
                API Number <span className="text-error-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="e.g., 42-003-01016"
                value={apiNumber}
                onChange={(e) => setApiNumber(e.target.value)}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter the 14-digit API number for the well
              </p>
            </div>

            {/* Jurisdiction Selection */}
            <div>
              <Label>
                Jurisdiction <span className="text-error-500">*</span>
              </Label>
              <select
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value as "TX" | "NM")}
                disabled={isLoading}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:focus:border-brand-400"
              >
                <option value="TX">Texas (TX) - W-3A Form</option>
                <option value="NM">New Mexico (NM) - C-103 Form</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select the regulatory jurisdiction for this well
              </p>
            </div>

            {/* Document Source Selection - Only for TX wells */}
            {jurisdiction === "TX" ? (
              <div>
                <Label>Document Source</Label>
                <div className="mt-3 space-y-3">
                  {[
                    { value: "extractions" as const, label: "RRC Extractions (Recommended)", desc: "Use extracted documents from RRC database" },
                    { value: "user_files" as const, label: "User Files", desc: "Upload your own documents" },
                    { value: "hybrid" as const, label: "Hybrid Mode", desc: "Combine RRC data with uploaded files" },
                    { value: "manual" as const, label: "Manual Entry", desc: "Enter well geometry data manually (no documents needed)" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="documentSource"
                        value={option.value}
                        checked={documentSource === option.value}
                        onChange={(e) => setDocumentSource(e.target.value as DocumentSource)}
                        disabled={isLoading}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                          {option.label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {option.desc}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Manual entry metadata fields */}
                {documentSource === "manual" && (
                  <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      These appear on the generated plan form. All fields are optional.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          District
                        </label>
                        <select
                          value={manualDistrict}
                          onChange={(e) => {
                            setManualDistrict(e.target.value);
                            setManualCounty(""); // Reset county when district changes
                          }}
                          disabled={isLoading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        >
                          <option value="">Select district</option>
                          {TX_DISTRICTS.map((d) => (
                            <option key={d.code} value={d.code}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          County
                        </label>
                        <select
                          value={manualCounty}
                          onChange={(e) => { setManualCounty(e.target.value); setManualField(""); }}
                          disabled={isLoading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        >
                          <option value="">Select county</option>
                          {(manualDistrict
                            ? getCountiesForDistrict("TX", manualDistrict)
                            : getAllCounties("TX")
                          ).map((county) => (
                            <option key={county} value={county}>{county}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Field
                        </label>
                        {getFieldsForCounty(manualCounty).length > 0 ? (
                          <>
                            <select
                              value={getFieldsForCounty(manualCounty).includes(manualField) ? manualField : (manualField ? "__other__" : "")}
                              onChange={(e) => {
                                if (e.target.value === "__other__") {
                                  setManualField("");
                                } else {
                                  setManualField(e.target.value);
                                }
                              }}
                              disabled={isLoading}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            >
                              <option value="">Select field</option>
                              {getFieldsForCounty(manualCounty).map((f) => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                              <option value="__other__">Other (type manually)</option>
                            </select>
                            {(!getFieldsForCounty(manualCounty).includes(manualField) && manualField !== "") && (
                              <input
                                type="text"
                                value={manualField}
                                onChange={(e) => setManualField(e.target.value)}
                                placeholder="Enter field name"
                                disabled={isLoading}
                                className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                              />
                            )}
                          </>
                        ) : (
                          <input
                            type="text"
                            value={manualField}
                            onChange={(e) => setManualField(e.target.value)}
                            placeholder={manualCounty ? "Enter field name" : "Select county first"}
                            disabled={isLoading}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Lease
                        </label>
                        <input
                          type="text"
                          value={manualLease}
                          onChange={(e) => setManualLease(e.target.value)}
                          placeholder="e.g., SMITH A"
                          disabled={isLoading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Well Number
                        </label>
                        <input
                          type="text"
                          value={manualWellNo}
                          onChange={(e) => setManualWellNo(e.target.value)}
                          placeholder="e.g., 1"
                          disabled={isLoading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Total Depth (ft)
                        </label>
                        <input
                          type="number"
                          value={manualTotalDepth}
                          onChange={(e) => setManualTotalDepth(e.target.value)}
                          placeholder="e.g., 10500"
                          disabled={isLoading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-full">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={manualHasUqw}
                            onChange={(e) => {
                              setManualHasUqw(e.target.checked);
                              if (!e.target.checked) setManualUqwBaseDepth("");
                            }}
                            disabled={isLoading}
                            className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                          />
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Well has Usable Quality Water (<AbbreviationTooltip term="UQW" />)
                          </span>
                        </label>
                        {manualHasUqw && (
                          <div className="mt-2 ml-6">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Base of UQW (ft)
                            </label>
                            <input
                              type="number"
                              value={manualUqwBaseDepth}
                              onChange={(e) => setManualUqwBaseDepth(e.target.value)}
                              placeholder="e.g., 500"
                              disabled={isLoading}
                              className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <p className="text-xs text-gray-400 mt-1">Depth of the deepest usable-quality water zone (from <AbbreviationTooltip term="GAU" /> letter or local knowledge)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* NM wells use OCD portal data - show info message */
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Data Source: <AbbreviationTooltip term="OCD" /> Portal
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      New Mexico wells use data scraped from the OCD (Oil Conservation Division) portal.
                      Document source options are not applicable for NM jurisdiction.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* NM C-103 START — NM C-103 Configuration */}
            {isNMWell && (
              <div className="mt-6 space-y-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 p-4">
                <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100">
                  NM C-103 Configuration
                </h3>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Region and <AbbreviationTooltip term="COA" /> Figure are auto-detected from the well county after lookup. Lease type is required for the C-103 form.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      COA Region
                    </label>
                    <input
                      value={nmRegion}
                      readOnly
                      placeholder="Auto-detected from county"
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      COA Figure
                    </label>
                    <input
                      value={nmCoaFigure}
                      readOnly
                      placeholder="Auto-detected"
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Lease Type
                    </label>
                    <select
                      value={nmLeaseType}
                      onChange={(e) => setNmLeaseType(e.target.value)}
                      disabled={isLoading}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    >
                      <option value="">Select lease type...</option>
                      <option value="state">State</option>
                      <option value="fee">Fee</option>
                      <option value="federal">Federal</option>
                      <option value="indian">Indian</option>
                    </select>
                  </div>
                  {nmRegion === 'south_hobbs' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sub-Area
                      </label>
                      <input
                        value={nmSubArea}
                        readOnly
                        placeholder="Auto-detected"
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* NM C-103 END */}

            {/* Plugs Mode Selection */}
            <div>
              <Label>Plugs Mode</Label>
              <div className="mt-3 space-y-3">
                {[
                  { value: "combined" as const, label: "Combined", desc: "Single merged plug recommendations" },
                  { value: "isolated" as const, label: "Isolated", desc: "Separate plugs for different zones" },
                  { value: "both" as const, label: "Both", desc: "Show both combined and isolated options" },
                ].map((option) => (
                  <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="plugsMode"
                      value={option.value}
                      checked={plugsMode === option.value}
                      onChange={(e) => setPlugsMode(e.target.value as PlugsMode)}
                      disabled={isLoading}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {option.desc}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Combine-mode geometry limits (max_plug_length_ft / max_gap_ft) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Max Plug Length (ft)</Label>
                <Input
                  type="number"
                  min="1"
                  step={1}
                  placeholder={String(jurisdictionMaxPlugLengthFt(jurisdiction))}
                  value={maxPlugLength}
                  onChange={(e) => setMaxPlugLength(e.target.value)}
                  disabled={isLoading}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum combined plug length when merging adjacent plugs. Leave blank for the{" "}
                  {jurisdiction === "NM" ? "NM limit (1,000 ft)" : "TX limit (1,250 ft)"}; the server never allows more than that.
                </p>
              </div>

              <div>
                <Label>Max Plug Spacing (ft)</Label>
                <Input
                  type="number"
                  min="0"
                  step={1}
                  placeholder="300"
                  value={maxGapFt}
                  onChange={(e) => setMaxGapFt(e.target.value)}
                  disabled={isLoading}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Adjacent plugs closer than this are combined into one. Leave blank for the default (300 ft); 0 never combines.
                </p>
              </div>
            </div>

            {/* File Uploads - Only show if not using RRC extractions exclusively */}
            {documentSource !== "extractions" && (
              <div className="border-t border-gray-200 pt-6 dark:border-white/[0.05]">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-4">
                  Upload Documents
                </h4>

                <div className="space-y-4">
                  {[
                    { key: "gau_file" as const, label: "GAU File", accept: ".pdf" },
                    { key: "w2_file" as const, label: "W-2 File", accept: ".pdf" },
                    { key: "w15_file" as const, label: "W-15 File", accept: ".pdf" },
                    { key: "schematic_file" as const, label: "Well Schematic", accept: ".pdf,.dwg,.dxf" },
                    { key: "formation_tops_file" as const, label: "Formation Tops", accept: ".pdf,.xlsx,.csv" },
                  ].map((fileField) => (
                    <div key={fileField.key}>
                      <Label>{fileField.label}</Label>
                      <FileInput
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          handleFileChange(fileField.key, file || null);
                        }}
                      />
                      {files[fileField.key] && (
                        <p className="mt-2 text-xs text-success-600 dark:text-success-400">
                          ✓ {files[fileField.key]!.name} ({(files[fileField.key]!.size / 1024).toFixed(1)}KB)
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

              <div className="flex justify-end">
                <Button
                  onClick={handleStartWizard}
                  disabled={isLoading || !apiNumber.trim()}
                >
                  {isLoading ? "Starting..." : "Start Plan Generation"}
                </Button>
          </div>
        </div>
          </div>
        )}

        {/* Step 2: Document Verification */}
        {currentStep === "documents" && initialResponse && (
          <>
            {/* NM-specific: Use NMDocumentsStep component */}
            {(initialResponse.jurisdiction === "NM" || jurisdiction === "NM") ? (
              <NMDocumentsStep
                initialResponse={initialResponse}
                onConfirm={handleConfirmDocuments}
                onBack={() => setCurrentStep("config")}
                isLoading={isLoading}
              />
            ) : (
              /* TX flow: Original document verification UI */
              <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    Verify Documents
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {initialResponse.page_count} pages from {initialResponse.source_files.length} document(s)
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  {/* Combined PDF Preview */}
                  {pdfBlobUrl ? (
                    <div className="border border-gray-200 rounded-lg p-6 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-center space-y-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/20 mb-2">
                        <svg className="w-8 h-8 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-1">
                          Combined Document Ready
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          {initialResponse.page_count} pages from {initialResponse.source_files.length} documents
                        </p>
                      </div>

                      <div className="flex justify-center gap-3">
                        <a
                          href={pdfBlobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Open PDF in New Tab
                        </a>
                        <a
                          href={pdfBlobUrl}
                          download={`combined_docs_${initialResponse.api}.pdf`}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download PDF
                        </a>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                        Review the combined document, then select which documents to include below
                      </p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-6 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <div className="w-full h-[200px] flex items-center justify-center">
                        <div className="text-center">
                          <div className="inline-block mb-4">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Combining documents...
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Source Files List */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">
                      Source Documents
                    </h4>
                    <div className="space-y-2">
                      {initialResponse.source_files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg dark:border-gray-700"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={!rejectedDocs.has(index)}
                              onChange={() => toggleDocumentRejection(index)}
                              className="rounded"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                {file.filename}
                              </p>
                              <p className="text-xs text-gray-500">
                                Pages {file.page_range[0]}-{file.page_range[1]} ({file.page_count} pages)
                              </p>
                            </div>
                          </div>
                          {rejectedDocs.has(index) && (
                            <span className="text-xs text-error-600 dark:text-error-400">Rejected</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button
                      onClick={() => setCurrentStep("config")}
                  disabled={isLoading}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Back
                </Button>
          <Button
                  onClick={handleConfirmDocuments}
                  disabled={isLoading}
          >
                  {isLoading ? "Extracting..." : "Confirm & Extract"}
          </Button>
        </div>
            </div>
          </div>
            )}
          </>
        )}

        {/* Step 3: Extraction Review */}
        {currentStep === "extractions" && extractionsResponse && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Review & Edit Extracted Data
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {extractionsResponse.extraction_count} document(s) extracted • Edit any values below
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* NM Warning Banner - Manual Entry Required */}
              {(extractionsResponse.jurisdiction === "NM" || jurisdiction === "NM" ||
                extractionsResponse.extractions.some(e => e.human_readable_summary?.requires_manual_entry)) && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-bold text-amber-900 dark:text-amber-100">
                        New Mexico Well - Manual Entry Required
                      </h4>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                        NM wells do not have casing or perforation data in public records. You will need to manually enter
                        casing strings and perforations in the next step (Geometry Review) for accurate plan generation.
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                        Have your well completion report, wellbore schematic, or C-103 on hand.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {extractionsResponse.extractions.map((extraction) => {
                const isExpanded = expandedDocuments.has(Number(extraction.extracted_document_id));
                
                return (
                  <div 
                    key={extraction.extracted_document_id} 
                    className="border border-gray-200 rounded-lg dark:border-gray-700 overflow-hidden"
                  >
                    {/* Document Header - Clickable to expand/collapse */}
                    <button
                      onClick={() => toggleDocumentExpansion(Number(extraction.extracted_document_id))}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="text-left">
                          <h4 className="text-base font-semibold text-gray-800 dark:text-white/90">
                            {extraction.filename}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Document Type: <span className="uppercase font-medium">{extraction.document_type}</span>
                            {extraction.extraction_status && ` • Status: ${extraction.extraction_status}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {isExpanded ? 'Click to collapse' : 'Click to expand'}
                      </div>
                    </button>

                    {/* Document Content - Collapsible */}
                    {isExpanded && (
                      <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                        {extraction.errors && extraction.errors.length > 0 && (
                          <div className="rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
                            <p className="text-xs font-semibold text-error-700 dark:text-error-400 mb-1">Extraction Errors:</p>
                            <ul className="text-xs text-error-600 dark:text-error-300 list-disc list-inside space-y-1">
                              {extraction.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Render extracted data in sections */}
                        {extraction.json_data && renderExtractionSections(extraction.json_data, Number(extraction.extracted_document_id))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={() => setCurrentStep("documents")}
                  disabled={isLoading}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Back
                </Button>
                <Button
                  onClick={handleConfirmExtractions}
                  disabled={isLoading}
                >
                  {isLoading ? "Processing..." : "Confirm & Continue"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Geometry Review */}
        {currentStep === "geometry" && geometryResponse && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Review Well Geometry
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Casing Strings - Show existing scraped data + manual entry for NM */}
              {geometryResponse.casing_strings.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">
                    Casing Strings
                  </h4>
                  <div className="space-y-2">
                    {geometryResponse.casing_strings.map((casing) => (
                      <div key={casing.field_id} className="p-3 border border-gray-200 rounded-lg dark:border-gray-700">
                        <p className="text-sm font-medium">{casing.field_label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(initialResponse?.jurisdiction === "NM" || jurisdiction === "NM" || documentSource === "manual") && (
                /* NM wells and manual mode - always show manual entry form to allow additions/overrides */
                <CasingEntryForm
                  casingStrings={userAddedCasing}
                  onAdd={(casing) => setUserAddedCasing((prev) => [...prev, casing])}
                  onRemove={(id) => setUserAddedCasing((prev) => prev.filter((c) => c.id !== id))}
                  onUpdate={(id, field, value) =>
                    setUserAddedCasing((prev) =>
                      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
                    )
                  }
                />
              )}

              {/* Formation Tops - CRITICAL for plan generation */}
              {geometryResponse.formation_tops.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h4 className="text-sm font-bold text-amber-900 dark:text-amber-100">
                      Formation Tops (Critical - Review Carefully)
                    </h4>
                  </div>
                  <p className="text-xs text-amber-800 dark:text-amber-200 mb-4">
                    These formations directly impact plug placement, count, and type. Verify depths are accurate.
                  </p>
                  
                  <div className="space-y-3">
                    {geometryResponse.formation_tops.map((formation) => {
                      const currentValue = getGeometryFieldValue(formation.field_id, formation.value as number | null);
                      
                      return (
                        <div key={formation.field_id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                                {formation.field_label}
                              </p>
                          </div>

                          <div className="flex items-center gap-3 mt-3">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 w-32">
                              Depth (ft):
                            </label>
                            <input
                              type="number"
                              value={currentValue !== null && currentValue !== undefined ? currentValue : ""}
                              onChange={(e) => {
                                const val = e.target.value ? parseFloat(e.target.value) : null;
                                updateGeometryField(formation.field_id, val);
                              }}
                              placeholder="Enter depth in feet"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            />
                            {currentValue !== formation.value && (
                              <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                                Modified
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
        </div>

                  {/* User-added formations */}
                  {userAddedFormations.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-amber-300 dark:border-amber-700">
                      <h5 className="text-xs font-bold text-amber-900 dark:text-amber-100 uppercase">
                        User-Added Formations
                      </h5>
                      {userAddedFormations.map((formation) => (
                        <div key={formation.id} className="bg-white dark:bg-gray-800 border-2 border-brand-300 dark:border-brand-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <input
                              type="text"
                              value={formation.name}
                              onChange={(e) => updateUserFormation(formation.id, "name", e.target.value)}
                              placeholder="Formation name"
                              className="flex-1 px-3 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            />
                            <button
                              onClick={() => removeUserFormation(formation.id)}
                              className="ml-3 text-error-600 hover:text-error-700 dark:text-error-400"
                              title="Remove formation"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 w-32">
                              Depth (ft):
                            </label>
                            <input
                              type="number"
                              value={formation.depth !== null && formation.depth !== undefined ? formation.depth : ""}
                              onChange={(e) => {
                                const val = e.target.value ? parseFloat(e.target.value) : null;
                                updateUserFormation(formation.id, "depth", val);
                              }}
                              placeholder="Enter depth in feet"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            />
                            <span className="text-xs text-brand-600 dark:text-brand-400 font-medium whitespace-nowrap">
                              User Added
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new formation form */}
                  <div className="pt-4 border-t border-amber-300 dark:border-amber-700 mt-4">
                    <h5 className="text-xs font-bold text-amber-900 dark:text-amber-100 uppercase mb-3">
                      Add Additional Formation
                    </h5>
                    <div className="bg-white dark:bg-gray-800 border border-dashed border-gray-400 dark:border-gray-600 rounded-lg p-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Formation Name
                          </label>
                          <input
                            type="text"
                            value={newFormationName}
                            onChange={(e) => setNewFormationName(e.target.value)}
                            placeholder="e.g., Wolfcamp A, Spraberry"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Depth (ft)
                          </label>
                          <input
                            type="number"
                            value={newFormationDepth}
                            onChange={(e) => setNewFormationDepth(e.target.value)}
                            placeholder="e.g., 7500"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                        <button
                          onClick={addUserFormation}
                          className="w-full px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Formation
                        </button>
                      </div>
                    </div>
                  </div>

                  {geometryResponse.formation_tops.length === 0 && userAddedFormations.length === 0 && (
                    <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">No formation tops found from policy or W-2.</p>
                      <p className="text-xs mt-1">Add formations above to generate formation-specific plugs.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Perforations - Show existing scraped data + manual entry for NM */}
              {geometryResponse.perforations && geometryResponse.perforations.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">
                    Perforations / Producing Intervals
                  </h4>
                  <div className="space-y-2">
                    {geometryResponse.perforations.map((perf) => (
                      <div key={perf.field_id} className="p-3 border border-gray-200 rounded-lg dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">{perf.field_label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(initialResponse?.jurisdiction === "NM" || jurisdiction === "NM" || documentSource === "manual") && (
                /* NM wells and manual mode - always show manual entry form to allow additions */
                <PerforationEntryForm
                  perforations={userAddedPerforations}
                  onAdd={(perf) => setUserAddedPerforations((prev) => [...prev, perf])}
                  onRemove={(id) => setUserAddedPerforations((prev) => prev.filter((p) => p.id !== id))}
                  onUpdate={(id, field, value) =>
                    setUserAddedPerforations((prev) =>
                      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
                    )
                  }
                />
              )}
              {(initialResponse?.jurisdiction === "NM" || jurisdiction === "NM" || documentSource === "manual") && (
                /* NM wells and manual mode - show historic cement job entry form */
                <CementJobEntryForm
                  cementJobs={userAddedCementJobs}
                  onAdd={(job) => setUserAddedCementJobs((prev) => [...prev, job])}
                  onRemove={(id) => setUserAddedCementJobs((prev) => prev.filter((j) => j.id !== id))}
                />
              )}

              {/* NM C-103 START — Required Formation Tops checklist for NM wells */}
              {isNMWell && (
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-4">
                  <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                    Required Formation Tops (NM C-103)
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-300 mb-4">
                    NM requires isolation of all formations in this region. Verify or add formation tops below. These will be included in the C-103 plan.
                  </p>
                  {nmFormationTops.length > 0 && (
                    <div className="overflow-x-auto mb-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-green-200 dark:border-green-700">
                            <th className="text-left py-2 px-2 text-xs font-medium text-green-800 dark:text-green-200">Formation</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-green-800 dark:text-green-200">Depth (ft)</th>
                            <th className="text-center py-2 px-2 text-xs font-medium text-green-800 dark:text-green-200">Required</th>
                            <th className="text-center py-2 px-2 text-xs font-medium text-green-800 dark:text-green-200">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nmFormationTops.map((ft, i) => (
                            <tr key={ft.id} className="border-b border-green-100 dark:border-green-900">
                              <td className="py-2 px-2">
                                <input
                                  type="text"
                                  value={ft.name}
                                  onChange={(e) => updateFormationTop(i, 'name', e.target.value)}
                                  placeholder="Formation name"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-brand-500"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <input
                                  type="number"
                                  value={ft.depth_ft !== null ? ft.depth_ft : ''}
                                  onChange={(e) => updateFormationTop(i, 'depth_ft', e.target.value ? Number(e.target.value) : null)}
                                  placeholder="Depth"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white text-right focus:ring-1 focus:ring-brand-500"
                                />
                              </td>
                              <td className="py-2 px-2 text-center text-xs text-green-700 dark:text-green-400 font-medium">
                                Required
                              </td>
                              <td className="py-2 px-2 text-center">
                                <button
                                  onClick={() => removeFormationTop(i)}
                                  className="text-error-600 hover:text-error-700 dark:text-error-400 text-xs underline"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <button
                    onClick={addFormationTop}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Formation
                  </button>
                  {nmFormationTops.length === 0 && (
                    <p className="mt-2 text-xs text-green-600 dark:text-green-400">
                      No NM formation tops added yet. Click "Add Formation" to specify required isolation intervals.
                    </p>
                  )}
                </div>
              )}
              {/* NM C-103 END */}

              {/* KOP (Kickoff Point) — directional wells only */}
              <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <h4 className="text-sm font-bold text-purple-900 dark:text-purple-100">
                    KOP — Kickoff Point
                  </h4>
                </div>
                <p className="text-xs text-purple-700 dark:text-purple-300 mb-4">
                  Depth (ft) at which the well deviates from vertical. Leave blank for vertical wells. Required for accurate directional well diagrams.
                </p>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 w-32 shrink-0">
                    KOP Depth (ft):
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={userKopDepth}
                    onChange={(e) => setUserKopDepth(e.target.value)}
                    placeholder="e.g. 3500 — leave blank if vertical"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  {userKopDepth && (
                    <button
                      onClick={() => setUserKopDepth("")}
                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-2"
                      title="Clear KOP"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {geometryResponse.kop_data?.kop_ft != null && userKopDepth === String(geometryResponse.kop_data.kop_ft) && (
                  <p className="mt-2 text-xs text-purple-600 dark:text-purple-400">
                    Extracted from documents: {geometryResponse.kop_data.kop_ft.toLocaleString()} ft
                  </p>
                )}
                {geometryResponse.kop_data?.kop_ft == null && !userKopDepth && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Not found in documents — enter manually if this is a directional well.
                  </p>
                )}
              </div>

              {/* Existing Mechanical Tools */}
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 016 0z" />
                  </svg>
                  <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100">
                    Existing Mechanical Tools
                  </h4>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mb-4">
                  Existing downhole tools (<AbbreviationTooltip term="CIBP" />, packers, retainers, etc.) that are already in the well.
                </p>

                {/* Existing tools from W-2 */}
                {geometryResponse.mechanical_barriers && geometryResponse.mechanical_barriers.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {geometryResponse.mechanical_barriers.map((tool) => (
                      <div key={tool.field_id} className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                              {tool.value.type} @ {tool.value.depth_ft} ft
                            </p>
                            {tool.value.description && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {tool.value.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* User-added tools */}
                {userAddedTools.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-blue-300 dark:border-blue-700">
                    <h5 className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase">
                      User-Added Tools
                    </h5>
                    {userAddedTools.map((tool) => (
                      <div key={tool.id} className="bg-white dark:bg-gray-800 border-2 border-brand-300 dark:border-brand-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                              {tool.type} @ {tool.depth} ft
                            </p>
                            {tool.description && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {tool.description}
                              </p>
                            )}
                            <p className="text-xs text-brand-600 dark:text-brand-400 font-medium mt-1">
                              ⭐ User Added (Existing Tool)
                            </p>
                          </div>
                          <button
                            onClick={() => setUserAddedTools(userAddedTools.filter(t => t.id !== tool.id))}
                            className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Remove tool"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new tool form */}
                <div className="pt-4 border-t border-blue-300 dark:border-blue-700">
                  <h5 className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase mb-3">
                    Add Existing Tool
                  </h5>
                  <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tool Type
                      </label>
                      <select
                        value={newToolType}
                        onChange={(e) => setNewToolType(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      >
                        <option value="CIBP">CIBP (Cast Iron Bridge Plug)</option>
                        <option value="Retrievable Bridge Plug">Retrievable Bridge Plug</option>
                        <option value="Cement Retainer">Cement Retainer</option>
                        <option value="Packer">Packer</option>
                        <option value="Liner Hanger">Liner Hanger</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Depth (ft MD)
                      </label>
                      <input
                        type="number"
                        value={newToolDepth}
                        onChange={(e) => setNewToolDepth(e.target.value)}
                        placeholder="e.g., 7500"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        value={newToolDescription}
                        onChange={(e) => setNewToolDescription(e.target.value)}
                        placeholder="e.g., Set during workover 2023"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (!newToolDepth || Number(newToolDepth) <= 0) {
                          alert("Please enter a valid depth");
                          return;
                        }
                        const newTool = {
                          id: `user_tool_${Date.now()}`,
                          type: newToolType,
                          depth: Number(newToolDepth),
                          description: newToolDescription,
                        };
                        setUserAddedTools([...userAddedTools, newTool]);
                        setNewToolDepth("");
                        setNewToolDescription("");
                      }}
                      className="w-full px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Existing Tool
                    </button>
                  </div>
                </div>

                {(!geometryResponse.mechanical_barriers || geometryResponse.mechanical_barriers.length === 0) && userAddedTools.length === 0 && (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">No existing mechanical tools found in W-2.</p>
                    <p className="text-xs mt-1">Add tools above if they exist in the well (e.g., CIBP, packers).</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setCurrentStep(documentSource === "manual" ? "config" : "extractions")}
                  disabled={isLoading}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Back
                </Button>
                <Button
                  onClick={handleConfirmGeometry}
                  disabled={isLoading}
                >
                  {isLoading ? "Building Plan..." : "Build Final Plan"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {currentStep === "complete" && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="p-6 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-100 dark:bg-success-900/20">
                <svg className="w-8 h-8 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Plan Generated Successfully!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Redirecting to plan view...
              </p>
            </div>

            {/* NM C-103 START — C-103 form header + compliance summary */}
            {isNMWell && (
              <div className="border-t border-gray-200 dark:border-white/[0.05] px-6 pb-6 pt-4 space-y-4">
                {/* C-103 form status */}
                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      C-103 Form
                    </h4>
                    {c103FormId ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700 dark:text-success-400 bg-success-100 dark:bg-success-900/20 px-2 py-0.5 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Created (ID: {c103FormId})
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Pending</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {nmRegion && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Region: </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{nmRegion}</span>
                      </div>
                    )}
                    {nmCoaFigure && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">COA Figure: </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{nmCoaFigure}</span>
                      </div>
                    )}
                    {nmLeaseType && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Lease Type: </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{nmLeaseType}</span>
                      </div>
                    )}
                    {nmSubArea && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Sub-Area: </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{nmSubArea}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Compliance checklist */}
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-4">
                  <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-3">
                    C-103 Compliance Summary
                  </h4>
                  <ul className="space-y-1.5 text-xs">
                    <li className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <svg className="w-4 h-4 text-success-600 dark:text-success-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Surface plug required
                    </li>
                    <li className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <svg className="w-4 h-4 text-success-600 dark:text-success-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Formation tops recorded: {nmFormationTops.length}
                    </li>
                    <li className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <svg className="w-4 h-4 text-success-600 dark:text-success-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      CIBP cap: 100 ft max enforced
                    </li>
                    <li className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <svg className="w-4 h-4 text-success-600 dark:text-success-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      NM OCD C-103 form generated
                    </li>
                  </ul>
                </div>

                {/* Save / Navigate actions for NM */}
                <div className="flex justify-end gap-3">
                  {c103FormId && (
                    <Button
                      onClick={() => navigate(`/c103/${c103FormId}`)}
                      variant="outline"
                      size="sm"
                    >
                      View C-103 Form
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* NM C-103 END */}
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="p-6">
              <div className="text-center space-y-4">
                <div className="inline-block">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
                </div>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {loadingMessage}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

    </>
  );
}
